import React, { useEffect, useState } from 'react';
import { Table, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { dedupedRequest } from '../../utils/axios';

const ClustersPanel = () => {
  const { token } = useAuth();
  const [clusters, setClusters] = useState<any[]>([]);
  const [dashboardRoles, setDashboardRoles] = useState<Array<{ id: number; name: string; usage: string }>>([]);
  const [formData, setFormData] = useState({
    name: '',
    color: '#FFFFFF',
    dashboard_role: 'none'
  });
  const [feedback, setFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const roleOptions = [{ id: -1, name: 'None', usage: 'none' }, ...dashboardRoles.filter((role) => role.usage !== 'none')];

  const loadClusters = async () => {
    dedupedRequest({ method: 'GET', url: '/admin/clusters' }, { useCache: true })
      .then(res => setClusters(res.data))
      .catch(err => console.error('Failed to load clusters', err));
  };

  const loadDashboardRoles = async () => {
    dedupedRequest({ method: 'GET', url: '/admin/dashboard-roles' }, { useCache: true })
      .then(res => setDashboardRoles(res.data || []))
      .catch(err => console.error('Failed to load dashboard roles', err));
  };

  useEffect(() => {
    loadClusters();
    loadDashboardRoles();
  }, [token]);

  const updateClusterRole = async (clusterId: number, dashboardRole: string) => {
    const response = await fetch(`/api/admin/clusters/${clusterId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ dashboard_role: dashboardRole === 'none' ? null : dashboardRole })
    });

    if (!response.ok) {
      let errorMessage = 'Diese Dashboard-Rolle kann nicht zugewiesen werden.';
      try {
        const errorPayload = await response.json();
        if (errorPayload?.message) {
          errorMessage = errorPayload.message;
        }
      } catch {
        // no-op: fallback to generic message
      }
      setFeedback({ type: 'danger', message: errorMessage });
      throw new Error(errorMessage);
    }

    const updatedCluster = await response.json();
    setFeedback({ type: 'success', message: 'Dashboard-Rolle gespeichert.' });
    setClusters((previous) => previous.map((cluster) => cluster.id === clusterId ? updatedCluster : cluster));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetch('/api/admin/clusters', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...formData,
        dashboard_role: formData.dashboard_role === 'none' ? null : formData.dashboard_role
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = 'Cluster konnte nicht gespeichert werden.';
          try {
            const errorPayload = await response.json();
            if (errorPayload?.message) {
              errorMessage = errorPayload.message;
            }
          } catch {
            // no-op: fallback to generic message
          }
          setFeedback({ type: 'danger', message: errorMessage });
          throw new Error(errorMessage);
        }
        const newCluster = await response.json();
        setFeedback({ type: 'success', message: 'Cluster wurde erfolgreich angelegt.' });
        setClusters((previous) => [...previous, newCluster]);
        setFormData({ name: '', color: '#FFFFFF', dashboard_role: 'none' });
      })
      .catch((error) => console.error('Failed to create cluster', error));
  };

  return (
    <>
      {feedback && (
        <Alert variant={feedback.type} className="mb-3" dismissible onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <h5>Clusters</h5>
      <Table striped bordered size="sm">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Color</th>
            <th>Dashboard role</th>
          </tr>
        </thead>
        <tbody>
          {clusters.map(c => (
            <tr key={c.id}>
              <td>{c.id}</td>
              <td>{c.name}</td>
              <td>
                <span
                  style={{
                    display: 'inline-block',
                    width: '1.5em',
                    height: '1.5em',
                    backgroundColor: c.color,
                    border: '1px solid #ccc'
                  }}
                />{' '}
                {c.color}
              </td>
              <td>
                <Form.Select
                  value={c.dashboard_role ?? 'none'}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    updateClusterRole(c.id, nextValue).catch((error) => console.error('Failed to update cluster role', error));
                  }}
                >
                  {roleOptions.map((role) => (
                    <option key={role.usage} value={role.usage}>{role.name}</option>
                  ))}
                </Form.Select>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <h5>Neuen Cluster anlegen</h5>
      <Form onSubmit={handleSubmit}>
        <Row className="mb-2">
          <Col xs="auto">
            <Form.Label>Farbe:</Form.Label>
          </Col>
          <Col xs="auto">
            <Form.Control
              type="color"
              value={formData.color}
              title="Farbe wählen"
              onChange={e => setFormData({ ...formData, color: e.target.value })}
            />
          </Col>
          <Col>
            <Form.Control
              placeholder="Cluster Name"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Col>
          <Col xs="auto">
            <Form.Select
              value={formData.dashboard_role}
              onChange={e => setFormData({ ...formData, dashboard_role: e.target.value })}
            >
              {roleOptions.map((role) => (
                <option key={role.usage} value={role.usage}>{role.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
        <Button type="submit">Cluster speichern</Button>
      </Form>
    </>
  );
};

export default ClustersPanel;
